import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router';
import { FeatureExtractionAPI } from '../../api/client';
import FeatureExtractionJobForm, { DEFAULT_QUALITY_THRESHOLD } from '../../components/Forms/FeatureExtraction/FeatureExtractionJobForm';
import PageWrapper from '../../components/Layout/PageWrapper';
import SkeletonJobForm from '../../components/Common/SkeletonJobForm';
import { useJobPolling } from '../../hooks/useJobPolling';

const FeatureExtractionJobDetailPage = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchJobData = useCallback(async () => {
    try {
      const jobData = await FeatureExtractionAPI.retrieve(id);
      setJob(jobData);

      // Only set form data if not already set to avoid overriding user edits
      if (!formData) {
        setFormData({
          slug: jobData.slug || '',
          name: jobData.name || '',
          description: jobData.description || '',
          quality_threshold: jobData.quality_threshold ?? DEFAULT_QUALITY_THRESHOLD
        });
      }
    } catch (e) {
      if (!job) setError('Failed to load feature extraction job');
    }
  }, [id, formData, job]);

  // Initial load
  useEffect(() => {
    const loadInitialJob = async () => {
      try {
        const jobData = await FeatureExtractionAPI.retrieve(id);
        setJob(jobData);
        setFormData({
          slug: jobData.slug || '',
          name: jobData.name || '',
          description: jobData.description || '',
          quality_threshold: jobData.quality_threshold ?? DEFAULT_QUALITY_THRESHOLD
        });
      } catch (e) {
        setError('Failed to load feature extraction job');
      } finally {
        setLoading(false);
      }
    };

    if (id) loadInitialJob();
  }, [id]);

  // Set up polling
  useJobPolling(job, fetchJobData);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        query: {
          data_source: `data/${formData.slug}/`,
          demographics_file_name: 'demographics.csv',
        },
        name: formData.name,
        description: formData.description,
        slug: formData.slug,
        quality_threshold: formData.quality_threshold,
      };

      const updatedJob = await FeatureExtractionAPI.update(id, payload);
      setJob(updatedJob);
      setIsEditing(false);
    } catch (e) {
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original job data
    setFormData({
      slug: job.slug || '',
      name: job.name || '',
      description: job.description || '',
      quality_threshold: job.quality_threshold ?? DEFAULT_QUALITY_THRESHOLD
    });
    setIsEditing(false);
    setError('');
  };

  const editControls = (
    <>
      <button
        onClick={handleSave}
        disabled={saving}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
      <button
        onClick={handleCancel}
        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
      >
        Cancel
      </button>
    </>
  );

  const viewControls = (
    <button
      onClick={() => setIsEditing(true)}
      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
    >
      Edit
    </button>
  );

  if (loading) return <SkeletonJobForm isNewJob={false} showEditButtons={isEditing} />;

  if (error && !job) {
    return (
      <PageWrapper>
        <div className="text-center py-12">
          <div className="text-red-600 text-lg">{error}</div>
        </div>
      </PageWrapper>
    );
  }

  if (!formData) return null;

  return (
    <FeatureExtractionJobForm
      formData={formData}
      setFormData={setFormData}
      isEditing={isEditing}
      job={job}
      title={`Feature Extraction > ${job?.name || `Job #${id}`}`}
      showEditButtons={true}
      editControls={isEditing ? editControls : viewControls}
      error={error}
    />
  );
};

export default FeatureExtractionJobDetailPage;
